import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { Stack, Text, TextLink } from '@eneco/sparky';

import type { SubFooterMenuContent } from './sub-footer-menu-content.types';

/**
 * `SubFooterMenu` block for the CMS-agnostic lane, serving **both** providers. Its content lives on the
 * referenced `siteWideContent` document: the copyright line as `title`, the legal links as `items`.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/Footer/SubFooterMenu.tsx`) — a bold
 * title over a wrapping `<ul>` of links — except that it still renders a lone title with no items, where
 * the legacy component returns null. Datasets exist in both states, and dropping the copyright line
 * because the links have not been migrated yet would be the worse failure.
 */
const SubFooterMenu: FC = () => {
  const content = useContent<SubFooterMenuContent>();

  const title = content.title ?? content.siteWideContent?.title;
  const items = (content.siteWideContent?.items ?? []).flatMap((item, index) => {
    const link = linkWithUrl(item.link);
    // An item whose link resolves to nothing has no destination to render — an unresolved internal
    // reference, most often. Dropped rather than rendered as inert text.
    return link ? [{ key: item._key ?? String(index), link }] : [];
  });

  if (!title && items.length === 0) return null;

  return (
    <Stack gap="2">
      {title && (
        <Stack.Item>
          <Text color="textPrimary" weight="bold">
            {title}
          </Text>
        </Stack.Item>
      )}
      {items.length > 0 && (
        <Stack.Item>
          <Stack as="ul" direction={{ initial: 'column', md: 'row' }} gap="3" wrap="true">
            {items.map(({ key, link }) => (
              <Stack.Item as="li" key={key}>
                <TextLink href={link.href} target={link.target} color="textPrimary">
                  {link.text ?? link.href}
                </TextLink>
              </Stack.Item>
            ))}
          </Stack>
        </Stack.Item>
      )}
    </Stack>
  );
};

export default SubFooterMenu;
